import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cn } from '../../lib/cn';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** Leading icon rendered inside the input's own border (DESIGN.md's
   * icon-prefixed input pattern — first used by the auth screens). Moves
   * the border/focus ring from the `<input>` onto a wrapping `<div>`. */
  icon?: ReactNode;
  /** Trailing slot inside the input's border, e.g. a password-visibility
   * toggle button. */
  endAdornment?: ReactNode;
  /** Content at the right end of the label row (e.g. "Forgot password?",
   * "Min. 12 characters") — the reference screens consistently pair a
   * label with a short trailing note/link on the same line. */
  labelExtra?: ReactNode;
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
}

/**
 * Label genuinely associated with its input, never placeholder-as-label
 * (Accessibility §8 — a placeholder disappears exactly when someone needs
 * it, mid-entry on a field with a validation error).
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, className, id, icon, endAdornment, labelExtra, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = [hint ? `${inputId}-hint` : null, error ? `${inputId}-error` : null]
    .filter(Boolean)
    .join(' ');

  const input = (
    <input
      ref={ref}
      id={inputId}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy || undefined}
      className={cn(
        icon || endAdornment
          ? 'h-full w-full bg-transparent px-0 text-base text-ink focus:outline-none'
          : 'h-9 rounded border border-line-strong bg-surface px-3 text-base text-ink focus-visible:border-accent',
        'placeholder:text-ink-subtle',
        !icon && !endAdornment && error && 'border-alert',
        className,
      )}
      {...props}
    />
  );

  return (
    <div className="flex flex-col gap-1.5">
      {labelExtra ? (
        <div className="flex items-center justify-between">
          <LabelPrimitive.Root htmlFor={inputId} className="text-sm font-medium text-ink">
            {label}
          </LabelPrimitive.Root>
          {labelExtra}
        </div>
      ) : (
        <LabelPrimitive.Root htmlFor={inputId} className="text-sm font-medium text-ink">
          {label}
        </LabelPrimitive.Root>
      )}
      {hint && (
        <p id={`${inputId}-hint`} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}
      {icon || endAdornment ? (
        <div
          className={cn(
            'flex h-9 items-center rounded border border-line-strong bg-surface pl-3 pr-1',
            'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/40',
            error && 'border-alert',
          )}
        >
          {icon && (
            <span aria-hidden className="mr-2 flex items-center text-ink-subtle">
              {icon}
            </span>
          )}
          {input}
          {endAdornment}
        </div>
      ) : (
        input
      )}
      {error && (
        <p id={`${inputId}-error`} className="text-xs text-alert">
          {error}
        </p>
      )}
    </div>
  );
});

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, hint, error, className, id, children, ...props },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const describedBy = [hint ? `${selectId}-hint` : null, error ? `${selectId}-error` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={selectId} className="text-sm font-medium text-ink">
        {label}
      </LabelPrimitive.Root>
      {hint && (
        <p id={`${selectId}-hint`} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          'h-9 rounded border border-line-strong bg-surface px-2 text-base text-ink',
          'focus-visible:border-accent',
          error && 'border-alert',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p id={`${selectId}-error`} className="text-xs text-alert">
          {error}
        </p>
      )}
    </div>
  );
});
